import type {
  ExportFileIssue,
  ExportFlowStep,
  GroupBy,
  PipingSystem,
} from '../../types/pcfExport.types';
import type { ActivePipeexProfile } from '../pipeex/pcfSettingsClient';
import {
  calculateDocumentPcf,
  calculatePickedPcf,
  type LocalPcfCalculationOutcome,
  type LocalPcfGroupFailure,
  type LocalPcfProgress,
} from './pcfExportCalculation';
import { prepareExportFlow, type ExportFlowContext } from './pcfExportContext';
import {
  recordFailedCalculation,
  recordLocalCalculation,
  refreshExportEntitlement,
  writeFiles,
  type ExportEntitlement,
} from './pcfExportDelivery';
import {
  createMappedHeaderWarning,
  logPcfMappingCoverage,
} from './pcfExportDiagnostics';
import { selectExportFilesForSystems } from './pcfExportSelection';

export type { ExportFlowStep } from '../../types/pcfExport.types';

export interface RunExportFlowOptions {
  groupBy: GroupBy;
  outputPath: string;
  /** Mirrors `ExportConfig.includeLinkedModels`; the document calculation
   *  must cover the same scope the system table was collected under. */
  includeLinkedModels?: boolean;
  /** Parameter-mode group labels selected in the table. Forwarded as the
   *  plugin's group filter (matched against group display names); empty or
   *  undefined exports every group. */
  groupFilter?: readonly string[];
  profile?: ActivePipeexProfile;
  pluginVersion?: string;
  onStep?: (step: ExportFlowStep, label: string) => void;
  onProgress?: (progress: LocalPcfProgress) => void;
}

export interface RunExportFlowResult {
  success: boolean;
  traceId: string;
  filesWritten?: number;
  skippedFiles?: string[];
  fileIssues?: ExportFileIssue[];
  successfulFiles?: string[];
  /** Exact total from the files the plugin confirmed it wrote. */
  elementsWritten?: number;
  /** Folder returned by the plugin after writing/validating the output. */
  outputPath?: string;
  step?: ExportFlowStep;
  error?: string;
  errorCode?: string;
  versionMismatch?: boolean;
  groupFailures?: LocalPcfGroupFailure[];
  quotaExhausted?: boolean;
  quotaInsufficient?: boolean;
  isDemo?: boolean;
  remainingExportElements?: number | null;
  mappedHeaderWarning?: string;
}

export interface RunPickExportFlowResult extends RunExportFlowResult {
  cancelled: boolean;
  elementsPicked: number;
}

const StepLabels = {
  license: 'Checking license and PCF export quota',
  calculate: 'Calculating PCF files in the PipeEX plugin',
  record: 'Recording the local PCF export run',
  write: 'Writing PCF files to disk',
} as const satisfies Record<ExportFlowStep, string>;

function reportStep(options: RunExportFlowOptions, step: ExportFlowStep): void {
  options.onStep?.(step, StepLabels[step]);
}

function withEntitlement(
  result: RunExportFlowResult,
  entitlement: ExportEntitlement,
): RunExportFlowResult {
  return {
    ...result,
    isDemo: result.isDemo ?? entitlement.isDemo,
    remainingExportElements: result.remainingExportElements === undefined
      ? entitlement.remainingExportElements
      : result.remainingExportElements,
  };
}

function releaseFiles(calculation: LocalPcfCalculationOutcome): void {
  calculation.files.length = 0;
}

async function finishFailedCalculation(
  context: ExportFlowContext,
  calculation: LocalPcfCalculationOutcome,
  options: RunExportFlowOptions,
  entitlement: ExportEntitlement,
): Promise<RunExportFlowResult> {
  // A terminal plugin result is trustworthy audit metadata even when it
  // produced no files. Recording it keeps the run traceable while the empty
  // confirmed-file set guarantees that no export elements are charged.
  reportStep(options, 'record');
  const recorded = await recordLocalCalculation(context, calculation, options, []);
  releaseFiles(calculation);
  const quota = recorded.ok ? recorded.value : entitlement;
  return {
    success: false,
    traceId: context.traceId,
    step: 'calculate',
    error: calculation.error || 'Local PCF calculation failed.',
    groupFailures: calculation.groupFailures,
    isDemo: quota.isDemo,
    remainingExportElements: quota.remainingExportElements,
    filesWritten: 0,
  };
}

async function deliverCalculatedFiles(
  context: ExportFlowContext,
  calculation: LocalPcfCalculationOutcome,
  filesToWrite: LocalPcfCalculationOutcome['files'],
  options: RunExportFlowOptions,
  entitlement: ExportEntitlement,
): Promise<RunExportFlowResult> {
  logPcfMappingCoverage(context.traceId, calculation.mappingCoverage);
  const mappedHeaderWarning = createMappedHeaderWarning(
    calculation.mappingCoverage,
    context.profileName,
  ) ?? undefined;

  reportStep(options, 'write');
  const written = await writeFiles(context, filesToWrite, options.outputPath);
  if (!written.ok) {
    // A zero-output receipt is audit metadata only: it is deliberately sent
    // after the write result and must not replace the user's write failure or
    // consume the pre-run entitlement.
    reportStep(options, 'record');
    await recordFailedCalculation(context, options, 'write');
    releaseFiles(calculation);
    return withEntitlement(written.failure, entitlement);
  }
  reportStep(options, 'record');
  const recorded = await recordLocalCalculation(
    context,
    calculation,
    options,
    written.value.confirmedFiles,
  );
  releaseFiles(calculation);
  if (!recorded.ok) {
    return withEntitlement({
      ...recorded.failure,
      ...written.value,
      groupFailures: calculation.groupFailures,
      mappedHeaderWarning,
    }, entitlement);
  }
  return {
    success: true,
    traceId: context.traceId,
    ...written.value,
    groupFailures: calculation.groupFailures,
    mappedHeaderWarning,
    isDemo: recorded.value.isDemo,
    remainingExportElements: recorded.value.remainingExportElements,
  };
}

async function prepareEntitledFlow(
  options: RunExportFlowOptions,
): Promise<
  | { readonly ok: true; readonly context: ExportFlowContext; readonly entitlement: ExportEntitlement }
  | { readonly ok: false; readonly failure: RunExportFlowResult }
> {
  const prepared = await prepareExportFlow(options.profile);
  if (!prepared.ok) {
    return prepared;
  }
  reportStep(options, 'license');
  const entitlement = await refreshExportEntitlement(prepared.value);
  if (!entitlement.ok) {
    return entitlement;
  }
  return { ok: true, context: prepared.value, entitlement: entitlement.value };
}

async function recordStreamFailure(
  context: ExportFlowContext,
  options: RunExportFlowOptions,
  failure: RunExportFlowResult,
  entitlement: ExportEntitlement,
): Promise<RunExportFlowResult> {
  void context;
  void options;
  return withEntitlement({ ...failure, filesWritten: 0 }, entitlement);
}

export async function runExportFlow(
  systems: PipingSystem[],
  options: RunExportFlowOptions,
): Promise<RunExportFlowResult> {
  const prepared = await prepareEntitledFlow(options);
  if (!prepared.ok) {
    return prepared.failure;
  }
  const { context, entitlement } = prepared;

  reportStep(options, 'calculate');
  const calculated = await calculateDocumentPcf(context, systems, options);
  if (!calculated.ok) {
    return recordStreamFailure(context, options, calculated.failure, entitlement);
  }
  if (!calculated.value.isSuccess) {
    return finishFailedCalculation(context, calculated.value, options, entitlement);
  }

  const selectedFiles = selectExportFilesForSystems(
    calculated.value.files,
    systems,
    options.groupBy,
    options.groupFilter,
  );
  return deliverCalculatedFiles(
    context,
    calculated.value,
    selectedFiles,
    options,
    entitlement,
  );
}

function asPickResult(
  result: RunExportFlowResult,
  elementsPicked: number,
  cancelled = false,
): RunPickExportFlowResult {
  return { ...result, cancelled, elementsPicked };
}

export async function runPickExportFlow(
  options: RunExportFlowOptions,
): Promise<RunPickExportFlowResult> {
  const prepared = await prepareEntitledFlow(options);
  if (!prepared.ok) {
    return asPickResult(prepared.failure, 0);
  }
  const { context, entitlement } = prepared;

  reportStep(options, 'calculate');
  const picked = await calculatePickedPcf(context, options);
  if (!picked.ok) {
    const failure = await recordStreamFailure(context, options, picked.failure, entitlement);
    return asPickResult(failure, 0);
  }
  if (picked.value.cancelled) {
    return asPickResult({
      success: true,
      traceId: context.traceId,
      isDemo: entitlement.isDemo,
      remainingExportElements: entitlement.remainingExportElements,
    }, picked.value.elementsPicked, true);
  }

  const calculation = picked.value.calculation;
  if (!calculation.isSuccess) {
    const failure = await finishFailedCalculation(context, calculation, options, entitlement);
    return asPickResult(failure, picked.value.elementsPicked);
  }
  const result = await deliverCalculatedFiles(
    context,
    calculation,
    calculation.files,
    options,
    entitlement,
  );
  return asPickResult(result, picked.value.elementsPicked);
}
